import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiSearch, FiCornerDownLeft, FiCode, FiStar, FiCommand } = FiIcons;
import SafeIcon from '../common/SafeIcon';
import { fuzzyScore } from '../utils/search';

export default function CommandPalette({ isOpen, onClose, results = [], resetNonce }) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    setQuery('');
    setSelectedIndex(0);
    if (isOpen) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, resetNonce]);

  const filtered = (results || [])
    .map(item => {
      if (!query) {
        const base = item.isPinned ? 1000 : item.type === 'primary' ? 400 : 0;
        return { ...item, _paletteScore: base };
      }
      const q = query.toLowerCase().trim();
      const label = (item.label || '').toLowerCase();
      const subtitle = (item.subtitle || '').toLowerCase();
      let score = 0;
      if (item.isPinned) score += 1000;
      if (item.type === 'primary') score += 250;
      if (label === q) score += 700;
      else if (label.startsWith(q)) score += 500;
      else if (label.includes(q)) score += 350;
      else score += fuzzyScore(label, q) * 2.5;
      if (subtitle.includes(q)) score += 120;
      else score += fuzzyScore(subtitle, q);
      return { ...item, _paletteScore: score };
    })
    .filter(item => !query || item._paletteScore > 0)
    .sort((a, b) => b._paletteScore - a._paletteScore)
    .slice(0, 12);

  // Safety: ensure selected index is within bounds of current results
  useEffect(() => {
    if (filtered.length === 0) {
      setSelectedIndex(0);
    } else if (selectedIndex >= filtered.length) {
      setSelectedIndex(filtered.length - 1);
    }
  }, [filtered.length]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }

    // Guard navigation if no results
    if (filtered.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = filtered[selectedIndex];
      if (selected?.action) {
        selected.action();
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: -20 }} 
        animate={{ opacity: 1, scale: 1, y: 0 }} 
        exit={{ opacity: 0, scale: 0.95, y: -20 }} 
        transition={{ duration: 0.15 }}
        className="w-full max-w-2xl bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/10"
      >
        <div className="flex items-center px-4 py-4 border-b border-gray-800 bg-gray-900/50">
          <SafeIcon icon={FiSearch} className="text-gray-400 text-xl mr-3" />
          <input 
            ref={inputRef}
            className="flex-1 bg-transparent border-none outline-none text-white text-lg placeholder-gray-500"
            placeholder="Search snippets or commands..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
          />
          <div className="flex items-center gap-1">
            <kbd className="px-2 py-1 rounded bg-gray-800 text-[10px] text-gray-400 font-bold border border-gray-700">ESC</kbd>
          </div>
        </div>

        <div className="max-h-[400px] overflow-y-auto py-2">
          {filtered.length > 0 ? (
            filtered.map((item, index) => (
              <div 
                key={item.id || index} 
                onClick={() => {
                  if (item.action) {
                    item.action();
                    onClose();
                  }
                }}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`px-4 py-3 flex items-center justify-between cursor-pointer transition-all ${
                  selectedIndex === index 
                    ? 'bg-indigo-600/20 border-l-4 border-indigo-500' 
                    : 'border-l-4 border-transparent'
                }`}
              >
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className={`p-2 rounded-lg ${
                    item.isPinned 
                      ? 'bg-yellow-500/10 text-yellow-500' 
                      : item.type === 'primary' 
                        ? 'bg-indigo-500/10 text-indigo-400' 
                        : 'bg-gray-800 text-gray-400'
                  }`}>
                    <SafeIcon icon={item.isPinned ? FiStar : item.type === 'primary' ? FiCode : FiCommand} className="text-sm" />
                  </div>
                  <div className="flex flex-col overflow-hidden">
                    <span className={`font-medium truncate ${selectedIndex === index ? 'text-white' : 'text-gray-200'}`}>
                      {item.label}
                    </span>
                    <span className="text-xs text-gray-500 truncate">{item.subtitle}</span>
                  </div>
                </div>
                {selectedIndex === index && (
                  <div className="flex items-center gap-2 text-indigo-400 text-[10px] font-bold uppercase tracking-wider">
                    <span className="bg-indigo-500/10 px-2 py-1 rounded flex items-center gap-1.5">
                      <SafeIcon icon={FiCornerDownLeft} /> Select
                    </span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="py-12 text-center text-gray-500 flex flex-col items-center gap-2">
              <SafeIcon icon={FiSearch} className="text-2xl opacity-20" />
              <span>No results found for "{query}"</span>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}